<template>
  <el-dialog
    :title="editing ? '编辑推理实例' : '新建推理实例'"
    :visible.sync="innerVisible"
    width="560px"
    @closed="resetForm"
  >
    <el-form :model="form" label-width="96px" size="small">
      <el-form-item label="实例名称">
        <el-input v-model="form.name" placeholder="留空则自动生成" clearable />
      </el-form-item>
      <el-form-item label="模型">
        <el-select v-model="form.model_id" placeholder="请选择模型" style="width:100%">
          <el-option
            v-for="m in models"
            :key="m.id"
            :label="m.name || m.file_path"
            :value="m.id"
          />
        </el-select>
        <div v-if="!models.length" class="form-hint">
          还没有模型，先在页面上传一个
        </div>
      </el-form-item>
      <el-form-item label="GPU">
        <el-select v-model="form.gpu_ids" multiple style="width:100%">
          <el-option
            v-for="g in gpus"
            :key="g.index"
            :label="`GPU ${g.index}${g.name ? ' · ' + g.name : ''}`"
            :value="g.index"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="置信度">
        <el-slider v-model="form.confidence" :min="0.01" :max="0.99" :step="0.01" show-input />
      </el-form-item>
      <el-form-item label="分辨率">
        <el-input-number v-model="form.resolution" :min="320" :max="880" :step="32" />
      </el-form-item>
      <el-form-item label="启用">
        <el-switch v-model="form.enabled" />
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createInstance, updateInstance } from '../api/blissrose/inference';

const DEFAULT_FORM = {
  id: '',
  name: '',
  model_id: '',
  gpu_ids: [],
  confidence: 0.5,
  resolution: 576,
  enabled: true,
};

/** 推理实例的新建 / 编辑对话框；提交成功只发 saved，由页面决定重载 */
export default {
  name: 'InstanceFormDialog',

  props: {
    visible: { type: Boolean, default: false },
    models: { type: Array, default: () => [] },
    gpus: { type: Array, default: () => [] },
    /** 要编辑的实例记录，为空表示新建 */
    editing: { type: Object, default: null },
    /** 新建时预选的模型 ID */
    presetModelId: { type: String, default: '' },
  },

  data() {
    return {
      submitting: false,
      form: { ...DEFAULT_FORM },
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
  },

  watch: {
    visible(val) {
      if (val) this.initForm();
    },
  },

  methods: {
    initForm() {
      if (this.editing) {
        const row = this.editing;
        this.form = {
          id: row.id,
          name: row.name || '',
          model_id: row.model_id || '',
          gpu_ids: [...(row.gpu_ids || [])],
          confidence: typeof row.confidence === 'number' ? row.confidence : DEFAULT_FORM.confidence,
          resolution: row.resolution || DEFAULT_FORM.resolution,
          enabled: row.enabled !== false,
        };
        return;
      }
      this.form = {
        ...DEFAULT_FORM,
        model_id: this.presetModelId,
        // 默认勾第一张卡，机器无卡时留 0 交后端判
        gpu_ids: this.gpus.length ? [this.gpus[0].index] : [0],
      };
    },

    resetForm() {
      this.form = { ...DEFAULT_FORM };
    },

    async handleSubmit() {
      if (!this.form.model_id) {
        this.$message.warning('请选择模型');
        return;
      }
      this.submitting = true;
      try {
        const { id, ...body } = this.form;
        if (id) {
          await updateInstance(id, body);
        } else {
          await createInstance(body);
        }
        this.$message.success(id ? '实例已更新' : '实例已创建');
        this.innerVisible = false;
        this.$emit('saved');
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.form-hint {
  color: #e6a23c;
  font-size: 12px;
  line-height: 18px;
}
</style>
